<template>
  <section aria-roledescription="carousel" class="relative flex flex-col">
    <div
      ref="carousel-scroller"
      :aria-label="isScrollable ? regionLabel : undefined"
      :role="isScrollable ? 'region' : undefined"
      :tabindex="isScrollable ? 0 : undefined"
      class="focus-visible-outline-inset-hi rounded-sm -mx-32 snap-none scroll-pl-32 overflow-x-auto overscroll-x-contain scroll-smooth transition-transform duration-700"
    >
      <RevCardCarouselItems
        ref="carousel-items"
        :card-classes
        :center="false"
        class="p-12 px-32"
        :items="itemsToRender"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { type VNode, computed, ref, useTemplateRef, watch } from 'vue'

import RevCardCarouselItems from '@ds/components/CardCarousel/components/CardCarouselItems.vue'
import type { SlotContent } from '@ds/types/SlotContent'
import { isVNodeEmpty, skipFragment } from '@ds/utils/vnode'
import { useResizeObserver, useSwipe } from '@vueuse/core'

defineProps<{
  cardClasses: string[] | string
  regionLabel: string
}>()

const slots = defineSlots<{
  default: SlotContent
}>()

const emit = defineEmits<{
  'slide-impression': [{ direction: 'backward' | 'forward' }]
}>()

const isScrollable = ref(false)

const carouselScrollerRef = useTemplateRef<HTMLDivElement>('carousel-scroller')
const { direction } = useSwipe(carouselScrollerRef, { threshold: 50 })

const itemsToRender = computed(() =>
  skipFragment(slots?.default({}) as VNode[]).filter(
    (vnode) => !isVNodeEmpty(vnode),
  ),
)

watch(direction, (dir) => {
  if (!dir || dir === 'none') return
  const slideDir = dir === 'left' ? 'forward' : 'backward'

  emit('slide-impression', { direction: slideDir })
})

useResizeObserver(carouselScrollerRef, (entries) => {
  const entry = entries[0]

  if (entry) {
    isScrollable.value = entry.target.scrollWidth > entry.target.clientWidth
  }
})
</script>
